import { useSyncExternalStore, type ReactNode, type CSSProperties } from "react";
import { SaltProvider, type Density } from "./core";

/*
 * Inter, in the four weights the ramp actually uses — 400 body, 500 for labels and table
 * headers, 600 for headings and buttons, 700 for the rare display figure. Loading the whole
 * family would ship nine weights to render four.
 *
 * `latin-` subsets rather than the full files: this product's UI is English, and the full
 * Inter covers Cyrillic and Greek that nothing here renders. Content in other scripts still
 * displays — it falls through to the system faces behind Inter in the stack.
 */
import "@fontsource/inter/latin-400.css";
import "@fontsource/inter/latin-500.css";
import "@fontsource/inter/latin-600.css";
import "@fontsource/inter/latin-700.css";
import "@fontsource/pt-mono/latin-400.css";
import "@salt-ds/theme/index.css";
import "@salt-ds/core/css/salt-core.css";
import "@salt-ds/icons/css/salt-icon.css";

export interface SaltFoundationProviderProps {
  readonly children: ReactNode;
  readonly density?: Density | "responsive";
  style?: CSSProperties;
}

/**
 * The `md` tier from index.css. Below 1024px the viewport is not a desktop.
 *
 * This MUST stay the complement of the `md` media queries in index.css. When it
 * sat at 959px and the shell gate sat at 1280px, every width between them got
 * desktop density with no sidebar.
 */
const MOBILE_DENSITY_QUERY = "(max-width: 1023px)";

/**
 * Devices with a coarse pointer (touchscreens) require larger tap targets.
 */
const POINTER_COARSE_QUERY = "(pointer: coarse)";

type QueryKey = "mobile" | "pointer";

const QUERY_REGISTRY: ReadonlyArray<readonly [QueryKey, string]> = [
  ["mobile", MOBILE_DENSITY_QUERY],
  ["pointer", POINTER_COARSE_QUERY],
] as const;

/**
 * Subscribe to a media query via useSyncExternalStore so the provider re-renders
 * without the tearing risks of useState/useEffect.
 */
function subscribeMediaQuery(
  query: string,
  callback: () => void,
): () => void {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return () => {};
  }

  const mql = window.matchMedia(query);
  mql.addEventListener("change", callback);
  return () => mql.removeEventListener("change", callback);
}

function getMediaQuerySnapshot(query: string): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia(query).matches;
}

function getServerSnapshot(): boolean {
  return false;
}

function useMediaQuery(query: string, enabled: boolean): boolean {
  return useSyncExternalStore(
    (callback) => (enabled ? subscribeMediaQuery(query, callback) : () => {}),
    () => (enabled ? getMediaQuerySnapshot(query) : false),
    getServerSnapshot,
  );
}

/**
 * Resolve the app's two supported responsive densities without coupling
 * product code to viewport logic.
 *
 * Mobile density requires BOTH a coarse pointer (touch device) AND a narrow
 * viewport (below 1024px). A narrow viewport alone on a desktop does not
 * trigger mobile density.
 *
 * Desktop runs Salt's `low` density, not `medium`. Salt's scale is
 * high 11px/20px, medium 12px/28px, low 14px/36px, touch 16px/44px — so
 * "low density" is Salt's comfortable desktop step, not a sparse one.
 * MEASURED on /corporate/440/playbook at 1670px wide: `medium` renders 12px
 * type inside ~38px rows, so the type is cramped WITHOUT buying back the
 * vertical space, because row height comes from padding rather than font
 * size. `low` puts type and control height back in agreement (14px type,
 * 36px controls) at a cost of roughly four table rows per screen.
 */
function useResponsiveSaltDensity(enabled = true): Density {
  const isMobileViewport = useMediaQuery(MOBILE_DENSITY_QUERY, enabled);
  const isTouchDevice = useMediaQuery(POINTER_COARSE_QUERY, enabled);

  if (!enabled) {
    return "low";
  }

  return isTouchDevice && isMobileViewport ? "mobile" : "low";
}

/**
 * Campaign Engine's public Salt theme boundary.
 *
 * Component CSS is bundled statically so Salt never needs runtime style-tag
 * injection. This keeps the provider compatible with strict CSP deployments.
 */
export function SaltFoundationProvider({
  children,
  density = "responsive",
}: SaltFoundationProviderProps) {
  const responsiveDensity = useResponsiveSaltDensity(density === "responsive");
  const resolvedDensity = density === "responsive" ? responsiveDensity : density;

  return (
    <SaltProvider
      applyClassesTo="root"
      density={resolvedDensity}
      enableStyleInjection={false}
      mode="light"
    >
      {children}
    </SaltProvider>
  );
}
